<template>
  <Modal ref="modalRef" class="choose-share-modal" :ok-disabled="!canShareRef" @ok="onShare">
    <template #title>
      Share a tree
    </template>
    <div>
      Choose a tree to share:
    </div>
    <div v-if="!dataGroupsRef.length" class="alert alert-primary alert-sm">
      <template v-if="excludeIdsRef.length">
        No other trees are available to share.
      </template>
      <template v-else>
        No trees are available to share.
      </template>
    </div>
    <ul class="list-unstyled">
      <li v-for="dg in dataGroupsRef" :key="dg.id">
        <ResearchGroupCard 
          :data-group-id="dg.id!"
          small 
          select-mode
          :selected-id="selectedDataGroupIdRef" 
          :status="getStatus(dg)"
          @selected="onSelected(dg.id!)">
        </ResearchGroupCard>
      </li>
    </ul>
    <div v-if="selectedIsLimitedRef" class="alert alert-warning alert-sm">
      The owner of the selected tree has a limited plan that does not allow sharing with groups managed by other users.
    </div>
    <template #okText>{{ shareTextRef }}</template>
    <template #busyText>Sharing...</template>
  </Modal>
</template>

<style scoped>
.subtitle {
  margin-bottom: 1rem;
  font-size: 0.875rem;
  color: #888;
}

:deep(.modal-body) {
  min-height: 240px;
  padding: 0 1rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

ul {
  padding-left: 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.alert {
  margin-top: auto;
}
</style>

<script setup lang="ts">
import { computed, ref } from "vue"
import { useDataGroupStore } from "@/gp/DataGroupStore"
import { useDataGroupMemberStore } from '@/gp/DataGroupMemberStore'
import { LoadMode } from "@/util/AsyncData"
import { useTokenManager } from "@/auth/TokenManager"
import { useTelemetry } from "@/util/Telemetry"
import { useUserStore } from "@/gp/UserStore"
import { DataGroup, DataGroupMember, DataGroupType, PrincipalType } from "@/gp/GroupAdminModel"
import { hideModalAsync, LimitedStatus } from "@/util/AppUtil"
import Modal from '@/util/Modal.vue'
import ResearchGroupCard from '@/manage/researchgroups/ResearchGroupCard.vue'

const emit = defineEmits([
  'requestShare'
])

defineExpose({
  open
})

const familyGroupIdRef = ref<string>()
const excludeIdsRef = ref<string[]>([])
const selectedDataGroupIdRef = ref<string>()

const modalRef = ref<InstanceType<typeof Modal>>()

const dataGroupStore = useDataGroupStore()
const dataGroupMemberStore = useDataGroupMemberStore()
const userStore = useUserStore()
const tokenManager = useTokenManager()
const { trackException } = useTelemetry()

const dataGroupsRef = computed(() => dataGroupStore.getVisibleGroups(LoadMode.EnsureLoaded)
  .filter(dg => dg.groupType == DataGroupType.Research && !excludeIdsRef.value.includes(dg.id!)))
const selectedDataGroupRef = computed(() => dataGroupsRef.value.find(dg => dg.id == selectedDataGroupIdRef.value))

const canShareRef = computed(() => !!selectedDataGroupRef.value && !selectedIsLimitedRef.value)
const isDataGroupOwnerRef = computed(() => selectedDataGroupRef.value?.ownerId == tokenManager.current.userId)
const useSharingRequestRef = computed(() => selectedDataGroupRef.value && !isDataGroupOwnerRef.value)
const shareTextRef = computed(() => useSharingRequestRef.value ? "Request share" : "Share")
const selectedIsLimitedRef = computed(() => {
  const owner = userStore.getAsyncUser(selectedDataGroupRef.value?.ownerId)?.data // loaded with DG
  return !isDataGroupOwnerRef.value && owner?.limits?.sharingLimit
})

function open(familyGroupId: string, excludeIds?: string[]) {
  familyGroupIdRef.value = familyGroupId
  excludeIdsRef.value = excludeIds ?? []
  selectedDataGroupIdRef.value = undefined
  modalRef.value?.open()
}

function getStatus(dataGroup: DataGroup) {
  if (dataGroup.ownerId == tokenManager.current.userId) {
    return undefined // show default status (in this case, "owner") for own trees
  }
  const owner = userStore.getAsyncUser(dataGroup.ownerId)?.data // loaded with DG
  return owner?.limits?.sharingLimit ? LimitedStatus : undefined
}

function onSelected(dataGroupId: string) {
  if (dataGroupId == selectedDataGroupIdRef.value) {
    selectedDataGroupIdRef.value = undefined // unselect
  } else {
    selectedDataGroupIdRef.value = dataGroupId
  }
}

async function onShare() {
  if (selectedDataGroupIdRef.value && selectedDataGroupRef.value?.ownerId == tokenManager.current.userId) {
    try {
      // share now
      const member = new DataGroupMember()
      member.dataGroupId = selectedDataGroupIdRef.value
      member.principalType = PrincipalType.FamilyGroup
      member.principalId = familyGroupIdRef.value!
      await dataGroupMemberStore.addAsync(selectedDataGroupIdRef.value, member)
      modalRef.value?.close()
    }
    catch (error) {
      trackException(error)
      modalRef.value?.reset()
    }
  }
  else {
    await hideModalAsync(modalRef.value!.$el)
    emit('requestShare', selectedDataGroupIdRef.value)
  }
}
</script>
